import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FAQ from "@/components/FAQ";
import ContactForm from "@/components/ContactForm";
import ScrollProgress from "@/components/ScrollProgress";
import { MessageCircle, ArrowRight } from "lucide-react";
import { FAQ_CATEGORIES } from "@/data/site";

export default function FAQPage() {
  const { t } = useTranslation();
  const total = FAQ_CATEGORIES.reduce((s, c) => s + c.items.length, 0);
  return (
    <div data-testid="faq-page" className="relative overflow-x-hidden">
      <ScrollProgress />
      <Navbar />
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid bg-grid-fade animate-grid-pulse pointer-events-none opacity-60" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="pill-mono inline-flex mb-5">
              {t("faq.page_eyebrow", "Knowledge Base · {count} entries").replace("{count}", total)}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-5">
              {t("faq.page_title1", "Frequently Asked")}{" "}
              <span className="text-gradient-brand font-semibold">{t("faq.page_titleAccent", "Questions")}</span>
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto text-sm lg:text-base">
              {t("faq.page_subtitle", "Every answer below is sourced directly from our official policy documents. Browse by category or jump straight to a topic.")}
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 max-w-3xl mx-auto">
              {FAQ_CATEGORIES.map((c, i) => (
                <motion.a
                  key={c.id}
                  href={`#${c.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                  data-testid={`faq-quick-${c.id}`}
                  className="card-beam px-3 py-3 hover:-translate-y-1 transition-transform"
                >
                  <div className="font-display text-xs lg:text-sm text-white">{t(`faq.cat_${c.id}`, c.label)}</div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <FAQ />

      {/* Still need help band */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-beam p-8 lg:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
          >
            <div>
              <div className="flex items-center gap-3 mb-2">
                <MessageCircle className="text-brand" size={20} />
                <h3 className="font-display text-xl lg:text-2xl font-medium">{t("faq.still_title", "Still have questions?")}</h3>
              </div>
              <p className="text-zinc-400 text-sm">{t("faq.still_body", "Drop us a line — our support team typically responds within a few hours.")}</p>
            </div>
            <a href="/#contact" className="btn-solid-brand text-sm">
              {t("faq.still_cta", "Contact support")} <ArrowRight size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
}
